<template>
  <entity-edit-dialog
      @submit="submit"
      @close="close"
      v-model="visible"
      ref="editDialog"
      :dialog-title="$t('corporationSettings.dialogTitle')"
      :submitting="submitting"
      :save-disabled="this.uploadingBackground || this.uploadingLogo"
      :max-width="700">
    <template v-slot:contentCard>
      <v-card-text class="pt-5">

        <entity-dialog-input-row :label="$t('corporationSettings.logo')">
          <div class="d-flex flex-row">
            <div>
              <img
                  v-if="logoFileName"
                  class="corporation-settings-dialog-selected-image"
                  v-bind:src="servedMediaAssetsUrl + '/logo/' + logoFileName"
                  :alt="'Logo file:' + logoFileName">
            </div>
            <div class="d-flex flex-column ml-3">
              <div class="mb-3">
                <input
                    type="file"
                    @change="logoFileSelected"
                    ref="logoSelect"
                    class="d-none"
                    accept="image/*"/>
                <v-btn
                    id="logo"
                    elevation="0"
                    x-small
                    @click="$refs.logoSelect.click()"
                    :disabled="submitting || uploadingLogo"
                    v-bind:loading="uploadingLogo">
                  <v-icon>mdi-upload</v-icon>
                  {{ $t('corporationSettings.uploadLogo') }}
                </v-btn>
              </div>
              <div>
              <v-btn
                  x-small
                  elevation="0"
                  @click="removeLogo()"
                  :disabled="!logoFileName || submitting || uploadingLogo">
                <v-icon>mdi-delete</v-icon>
                {{ $t('actions.delete') }}
              </v-btn>
              </div>
            </div>

          </div>

        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('corporationSettings.background')">
          <div class="d-flex flex-row">
            <div>
              <img
                  v-if="backgroundFileName"
                  class="corporation-settings-dialog-selected-image"
                  v-bind:src="servedMediaAssetsUrl + '/background/' + backgroundFileName"
                  :alt="'Background file:' + backgroundFileName">

            </div>
            <div class="d-flex flex-column ml-3">
              <div class="mb-3">
                <input
                    type="file"
                    @change="backgroundFileSelected"
                    ref="backgroundSelect"
                    class="d-none"
                    accept="image/*"/>
                <v-btn
                    id="background"
                    x-small
                    elevation="0"
                    @click="$refs.backgroundSelect.click()"
                    :disabled="submitting || uploadingBackground"
                    v-bind:loading="uploadingBackground">
                  <v-icon>mdi-upload</v-icon>
                  {{ $t('corporationSettings.uploadBackground') }}
                </v-btn>
              </div>
              <div>
                <v-btn
                    x-small
                    elevation="0"
                    @click="removeBackground()"
                    :disabled="!backgroundFileName || submitting || uploadingBackground">
                  <v-icon>mdi-delete</v-icon>
                  {{ $t('actions.delete') }}
                </v-btn>
              </div>
            </div>
           </div>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('corporationSettings.backgroundTransparency')">
          <v-slider
              id="backgroundTransparency"
              v-model="backgroundTransparencyPercent"
              min="0"
              max="100"
              @change="previewInUse()"
          />
        </entity-dialog-input-row>

      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityDialogInputRow from '@/components/entity-crud/EntyDialogInputRow';
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import CorporationSettingsService from '@/services/CorporationSettingsService';

export default {
  name: 'CorporationSettingsDialog',
  components: {
    EntityEditDialog,
    EntityDialogInputRow,
  },
  data: () => ({
    visible: false,
    submitting: false,

    servedMediaAssetsUrl: CorporationSettingsService.getServedMediaAssetsUrl(),
    uploadingLogo: false,
    uploadingBackground: false,

    logoFileName: null,
    backgroundFileName: null,
    backgroundTransparencyPercent: 50,
  }),
  props: {},
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    close() {
      this.corporation = null;
      this.visible = false;
      this.submitting = false;
      this.$store.dispatch('corporationSettingsState/update');
    },

    open() {
      let corporationSettings = this.$store.getters['corporationSettingsState/corporationSettings'];

      this.logoFileName = corporationSettings.logoFileName;
      this.backgroundFileName = corporationSettings.backgroundFileName;

      if (!corporationSettings.backgroundTransparencyPercent && corporationSettings.backgroundTransparencyPercent !== 0) {
        this.backgroundTransparencyPercent = 70;
      } else {
        this.backgroundTransparencyPercent = corporationSettings.backgroundTransparencyPercent;
      }

      this.visible = true;
    },

    toCorporationSettings() {
      return {
        logoFileName: this.logoFileName,
        backgroundFileName: this.backgroundFileName,
        backgroundTransparencyPercent: this.backgroundTransparencyPercent,
      };
    },

    previewInUse() {
      this.$store.commit('corporationSettingsState/SET', this.toCorporationSettings());
    },


    submit() {
      if (this.submitting || this.uploadingLogo || this.uploadingBackground) {
        return;
      }


      this.submitting = true;

      CorporationSettingsService.save(this.toCorporationSettings())
          .then(() => {
            this.$store.dispatch('corporationSettingsState/update');
            this.submitting = false;
            this.$emit('done');
            this.close();
          })
          .catch(err => {
            console.log(JSON.stringify(err));
          })
          .finally(() => {
            this.submitting = false;
          });
    },


    logoFileSelected(e) {

      let selectedLogoFile = null;
      if (e.target.files.length) {
        selectedLogoFile = e.target.files[0];
      }

      if (!selectedLogoFile) {
        return;
      }

      this.uploadingLogo = true;
      CorporationSettingsService.uploadLogo(selectedLogoFile)
          .then((response) => {
            this.uploadingLogo = false;
            this.logoFileName = response.data.fileName;
            this.previewInUse();
          })
          .catch(err => {
            console.log(JSON.stringify(err));
          })
          .finally(() => {
            this.uploadingLogo = false;
          });
    },
    removeLogo() {
      this.logoFileName = null;
      this.previewInUse();
    },

    backgroundFileSelected(e) {

      let selectedBackgroundFile = null;
      if (e.target.files.length) {
        selectedBackgroundFile = e.target.files[0];
      }

      if (!selectedBackgroundFile) {
        return;
      }

      this.uploadingBackground = true;
      CorporationSettingsService.uploadBackground(selectedBackgroundFile)
          .then((response) => {
            this.uploadingBackground = false;
            this.backgroundFileName = response.data.fileName;
            this.previewInUse();
          })
          .catch(err => {
            console.log(JSON.stringify(err));
          })
          .finally(() => {
            this.uploadingBackground = false;
          });
    },

    removeBackground() {
      this.backgroundFileName = null;
      this.previewInUse();
    },
  },
};
</script>

<style scoped>
.repeat-weekdays > * {
  margin-right: 1em;
}
</style>
